import { Divider } from '@mui/material';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import dayjs, { Dayjs } from 'dayjs';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { capitalizeFirstLetter } from '../../utils/capitalize';
import ModalBody, { ModalProps } from './ModalBody';

interface CalendarRangeModalProps extends ModalProps {
    startDate: Date;
    endDate: Date;
    setStartDate: (newDate: string) => void;
    setEndDate: (newDate: string) => void;
}

const CalendarRangeModal: React.FC<CalendarRangeModalProps> = ({ startDate, endDate, setStartDate, setEndDate, open, handleClose }) => {
    const [selectedStartDate, setSelectedStartDate] = useState(startDate);
    const [selectedEndDate, setSelectedEndDate] = useState(endDate);
    const { t } = useTranslation();

    const handleStartDateChange = (value: Date | null) => {
        if (value) {
            setSelectedStartDate(value);
            if (selectedEndDate && value > selectedEndDate) {
                // If the start date is after the end date, reset the end date
                setSelectedEndDate(value);
                setEndDate(value.toISOString());
            }
        }
    };

    const handleEndDateChange = (value: Date | null) => {
        if (value) {
            setSelectedEndDate(value);
            if (selectedStartDate && value < selectedStartDate) {
                // If the end date is before the start date, reset the start date
                setSelectedStartDate(value);
                setStartDate(value.toISOString());
            }
        }
    };

    const disableDatesAfter = (date: Dayjs) => {
        return date.isAfter(dayjs(selectedEndDate), 'day');
    };

    const disableDatesBefore = (date: Dayjs) => {
        return date.isBefore(dayjs(selectedStartDate), 'day');
    };

    return (
        <ModalBody
            title={t('calendar')}
            open={open}
            handleClose={handleClose}
            onSubmit={() => {
                setStartDate(selectedStartDate.toISOString());
                setEndDate(selectedEndDate.toISOString());
            }}
            width={'fit-content'}
        >
            <div className="fx-r gap-20px">
                <div>
                    <h3 className="m-0">{capitalizeFirstLetter(t('startDate'))}</h3>
                    <DateCalendar
                        onChange={handleStartDateChange}
                        value={dayjs(selectedStartDate)}
                        displayWeekNumber
                        shouldDisableDate={disableDatesAfter}
                    />
                </div>
                <Divider orientation="vertical" flexItem variant="middle" />
                <div>
                    <h3 className="m-0">{capitalizeFirstLetter(t('endDate'))}</h3>
                    <DateCalendar
                        onChange={handleEndDateChange}
                        value={dayjs(selectedEndDate)}
                        displayWeekNumber
                        shouldDisableDate={disableDatesBefore}
                    />
                </div>
            </div>
        </ModalBody>
    );
};

export default CalendarRangeModal;
